import React from 'react';
import Card, { CardContent } from '../../components/ui/Card';

const AcceptableUsePolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Acceptable Use Policy</h1>
            <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

            <div className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Purpose</h2>
              <p className="text-gray-700 mb-4">
                This Acceptable Use Policy defines the rules and guidelines for using Patreon Clone. Violations may 
                result in content removal, account suspension, or permanent ban from the platform.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Prohibited Content</h2>
              <p className="text-gray-700 mb-4">
                The following types of content are strictly prohibited on our platform:
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Illegal Content</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Content that violates local, state, federal, or international laws</li>
                <li>Promotion or facilitation of illegal activities</li>
                <li>Distribution of controlled substances</li>
                <li>Illegal gambling operations</li>
                <li>Counterfeit goods or services</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Harmful or Dangerous Content</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Content promoting violence, terrorism, or extremism</li>
                <li>Instructions for creating weapons or explosives</li>
                <li>Content that encourages self-harm or suicide</li>
                <li>Dangerous challenges or pranks that risk physical harm</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.3 Hateful Content</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Hate speech targeting race, ethnicity, religion, gender, sexual orientation, or disability</li>
                <li>Content promoting discrimination or prejudice</li>
                <li>Symbols or imagery associated with hate groups</li>
                <li>Holocaust denial or other historical revisionism aimed at hatred</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.4 Sexual Content</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Child sexual abuse material (CSAM) in any form</li>
                <li>Non-consensual intimate imagery (revenge porn)</li>
                <li>Sexual services or prostitution</li>
                <li>Explicit sexual content involving minors (real or fictional)</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>Note:</strong> Adult content for consenting adults is permitted with appropriate age verification 
                and content warnings, subject to our Adult Content Guidelines.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.5 Harassment and Bullying</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Targeted harassment of individuals or groups</li>
                <li>Cyberbullying or intimidation</li>
                <li>Doxxing (sharing private personal information)</li>
                <li>Coordinated harassment campaigns</li>
                <li>Stalking or unwanted contact</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.6 Misinformation</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Deliberate spread of false information (disinformation)</li>
                <li>Medical misinformation that could cause harm</li>
                <li>Election interference or voter suppression</li>
                <li>Manipulated media intended to deceive (deepfakes)</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. Prohibited Behavior</h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 Fraud and Deception</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Impersonating other individuals or organizations</li>
                <li>Phishing or social engineering attacks</li>
                <li>Pyramid schemes or multi-level marketing</li>
                <li>False advertising or misleading claims</li>
                <li>Chargebacks fraud or payment manipulation</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.2 Platform Abuse</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Creating multiple accounts to circumvent bans</li>
                <li>Artificially inflating metrics (fake patrons, bots)</li>
                <li>Spamming or excessive messaging</li>
                <li>Scraping or unauthorized data collection</li>
                <li>Attempting to hack or compromise platform security</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.3 Intellectual Property Violations</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Copyright infringement (unauthorized use of protected works)</li>
                <li>Trademark violations</li>
                <li>Distribution of pirated content</li>
                <li>Selling counterfeit goods</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Creator-Specific Rules</h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Benefit Fulfillment</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>You must deliver all promised benefits to patrons</li>
                <li>Benefits must be delivered in a timely manner</li>
                <li>You cannot charge for content and then delete it without providing refunds</li>
                <li>Pausing your account does not absolve benefit obligations</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Transparency Requirements</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Clearly describe what patrons will receive at each tier</li>
                <li>Disclose any material changes to benefits</li>
                <li>Be transparent about your posting schedule and commitments</li>
                <li>Communicate absences or delays to patrons</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Patron Responsibilities</h2>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Do not share or redistribute exclusive creator content</li>
                <li>Do not abuse dispute/chargeback processes</li>
                <li>Respect creator boundaries and communication preferences</li>
                <li>Do not harass creators for refunds outside platform policies</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Reporting Violations</h2>
              <p className="text-gray-700 mb-4">
                If you encounter content or behavior that violates this policy:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Use the "Report" button on content or profiles</li>
                <li>Provide detailed information about the violation</li>
                <li>Email trust-and-safety@patreonclone.com for urgent matters</li>
                <li>For CSAM or imminent threats, also contact law enforcement</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Enforcement</h2>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.1 Review Process</h3>
              <p className="text-gray-700 mb-4">
                Reports are reviewed by our Trust & Safety team. We aim to respond within 48 hours for standard 
                violations and immediately for emergency situations.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.2 Consequences</h3>
              <p className="text-gray-700 mb-4">
                Depending on severity and history, violations may result in:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Warning:</strong> First-time minor violations</li>
                <li><strong>Content Removal:</strong> Specific content that violates policies</li>
                <li><strong>Temporary Suspension:</strong> 7-30 days for moderate violations</li>
                <li><strong>Permanent Ban:</strong> Severe or repeated violations</li>
                <li><strong>Legal Action:</strong> Criminal activity reported to authorities</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.3 Appeals</h3>
              <p className="text-gray-700 mb-4">
                If you believe your content was removed in error, you may appeal by contacting 
                appeals@patreonclone.com within 30 days of the action.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Platform Discretion</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Modify this policy at any time with notice</li>
                <li>Take action on content not explicitly listed here</li>
                <li>Exercise discretion in enforcement decisions</li>
                <li>Refuse service to anyone for any lawful reason</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Legal Obligations</h2>
              <p className="text-gray-700 mb-4">
                We comply with all applicable laws including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>DMCA (Digital Millennium Copyright Act)</li>
                <li>FOSTA-SESTA (anti-sex trafficking laws)</li>
                <li>COPPA (Children's Online Privacy Protection Act)</li>
                <li>GDPR (for EU users)</li>
                <li>Local content regulations in operating jurisdictions</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Contact Information</h2>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700"><strong>General Questions:</strong> support@patreonclone.com</p>
                <p className="text-gray-700"><strong>Report Violations:</strong> trust-and-safety@patreonclone.com</p>
                <p className="text-gray-700"><strong>Appeals:</strong> appeals@patreonclone.com</p>
                <p className="text-gray-700"><strong>Copyright Issues:</strong> dmca@patreonclone.com</p>
              </div>

              <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Remember:</strong> This platform is built on trust and community. Help us maintain a safe, 
                  welcoming environment by following these guidelines and reporting violations when you see them.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptableUsePolicyPage;
